/**
 * Drawer Constants
 *
 * Shared constants for the layered drawer system.
 * Separated from component for fast refresh compatibility.
 */

// ============================================================================
// Z-Index Strategy
// ============================================================================

/**
 * Z-Index Strategy:
 * - ProjectModal base: 180
 * - Each drawer layer adds 40
 * - Backdrop is 10 below its drawer
 *
 * Layer 1: backdrop=210, drawer=220
 * Layer 2: backdrop=250, drawer=260
 * Layer 3: backdrop=290, drawer=300
 */
export const Z_INDEX_BASE = 180;
export const Z_INDEX_INCREMENT = 40;

/**
 * Visual offset per layer (creates stacked cards effect)
 * Used for contained mode to show layering between drawers
 * Increased to 32px for better visual separation
 */
export const OFFSET_PX = 32;

/**
 * Left margin for contained drawers (shows peek of content behind)
 * This creates the "peek" effect showing the overview behind the drawer
 */
export const CONTAINED_LEFT_MARGIN = 48;

/**
 * Animation duration in ms - matched to SlidePanel for consistency
 */
export const ANIMATION_DURATION_MS = 350;

/**
 * Animation timing for opening (ease-out-expo for smooth deceleration)
 */
export const ANIMATION_EASE_IN = "cubic-bezier(0.16, 1, 0.3, 1)";

/**
 * Animation timing for closing (ease-in for quick exit)
 */
export const ANIMATION_EASE_OUT = "cubic-bezier(0.4, 0, 1, 1)";

// ============================================================================
// Utility Functions
// ============================================================================

/** Calculate z-index for drawer at given layer */
export function getZIndex(layer: number): number {
  return Z_INDEX_BASE + layer * Z_INDEX_INCREMENT;
}

/** Calculate z-index for backdrop at given layer */
export function getBackdropZIndex(layer: number): number {
  return getZIndex(layer) - 10;
}

/** Calculate horizontal offset for visual stacking */
export function getOffset(layer: number): number {
  return layer * OFFSET_PX;
}

// ============================================================================
// Width Classes
// ============================================================================

export type DrawerWidth = "sm" | "md" | "lg" | "xl" | "full";

export const WIDTH_CLASSES: Record<DrawerWidth, string> = {
  sm: "w-[400px]",
  md: "w-[500px]",
  lg: "w-[600px]",
  xl: "w-[800px]",
  full: "w-full max-w-[900px]",
};
